import { createRouter, createWebHistory } from "vue-router";
import multiguard from "vue-router-multiguard";
import { i18n, loadLocaleMessages } from "@/plugins/i18n";
import {
  initialiseApplication,
  userMustBeAuthenticated,
  userMustNotBeAuthenticated,
  merchantMustNotBeVerified,
  userMustBeStaff,
  userMustBeConfigured,
  requiredSitePermissions,
  requiredCompanyPermissions,
  canManageCompanies,
  requiredAnySitePermissions,
  userMustBeNativeApp,
  userMustBeAuthenticatedNativeAppUser,
} from "@/router/navigation-guards";
import { Permission } from "@/store/authorization";

declare module "vue-router" {
  interface RouteMeta {
    breadcrumbs?: { titleKey: string; href?: string }[];
  }
}

const routes = [
  {
    path: "/public",
    component: () => import("@/layouts/Full.vue"),
    children: [
      {
        path: "/signin",
        name: "sign-in",
        beforeEnter: multiguard([userMustNotBeAuthenticated]),
        component: () => import("@/views/Authentication/SignIn.vue"),
      },
      {
        path: "/signup",
        name: "sign-up",
        beforeEnter: multiguard([userMustNotBeAuthenticated]),
        component: () => import("@/views/Authentication/SignUp.vue"),
      },
      {
        path: "/join",
        name: "join",
        beforeEnter: multiguard([userMustNotBeAuthenticated]),
        component: () => import("@/views/Onboarding/Join.vue"),
        meta: { fullLayoutBackgroundColor: "bg-pink-5", fullLayoutLogoLeftAligned: true },
      },
      {
        path: "/forgot",
        name: "forgot-password",
        beforeEnter: multiguard([userMustNotBeAuthenticated]),
        component: () => import("@/views/Authentication/ForgotPassword.vue"),
      },
      {
        path: "/oops",
        name: "not-right",
        component: () => import("@/views/NotRight.vue"),
      },
      {
        path: "/payment-method-success",
        name: "payment-method-success",
        component: () => import("@/views/PaymentMethodSuccess.vue"),
      },
    ],
  },
  {
    path: "/app",
    component: () => import("@/layouts/App.vue"),
    beforeEnter: multiguard([userMustBeAuthenticated, initialiseApplication, userMustBeConfigured]),
    children: [
      {
        path: "/",
        name: "your-basecamp",
        component: () => import("@/views/AppLanding.vue"),
        meta: { hideBillingWarningBanner: true },
      },
      {
        path: "/billing/payment-methods",
        name: "payment-methods",
        component: () => import("@/views/Billing/BillingSettings.vue"),
        meta: {
          breadcrumbs: [
            { titleKey: "breadcrumbs.billing" },
            { titleKey: "breadcrumbs.paymentMethods" },
          ],
        },
        beforeEnter: multiguard([
          requiredCompanyPermissions([Permission.Billing_ListPaymentMethods]),
        ]),
      },
      {
        path: "/billing/payment-methods/new",
        name: "add-payment-method",
        component: () => import("@/views/Billing/AddPaymentMethod.vue"),
        meta: {
          breadcrumbs: [
            { titleKey: "breadcrumbs.billing" },
            { titleKey: "breadcrumbs.paymentMethods", href: "/billing/payment-methods" },
            { titleKey: "breadcrumbs.new" },
          ],
          hideBillingWarningBanner: true,
        },
        beforeEnter: multiguard([
          requiredCompanyPermissions([Permission.Billing_AddPaymentMethod]),
        ]),
      },
      {
        path: "/billing/invoices",
        name: "invoices",
        component: () => import("@/views/Billing/Invoices.vue"),
        meta: {
          breadcrumbs: [{ titleKey: "breadcrumbs.billing" }, { titleKey: "breadcrumbs.invoices" }],
        },
        beforeEnter: multiguard([requiredCompanyPermissions([Permission.Billing_ListInvoices])]),
      },
      {
        path: "/devices",
        name: "all-devices",
        component: () => import("@/views/Devices/Devices.vue"),
        meta: { breadcrumbs: [{ titleKey: "breadcrumbs.devices" }] },
      },
      {
        path: "/request-new-device",
        name: "order-new-device",
        component: () => import("@/views/Devices/NewDeviceRequestWizard.vue"),
        meta: {
          breadcrumbs: [
            { titleKey: "breadcrumbs.devices", href: "/devices" },
            { titleKey: "breadcrumbs.orderNew" },
          ],
          hideBillingWarningBanner: true,
        },
        beforeEnter: multiguard([requiredCompanyPermissions([Permission.Device_OrderDevice])]),
      },
      {
        path: "/assign-new-device",
        name: "connect-new-device",
        component: () => import("@/views/Devices/AssignDevice.vue"),
        meta: {
          breadcrumbs: [
            { titleKey: "breadcrumbs.devices", href: "/devices" },
            { titleKey: "breadcrumbs.assignNew" },
          ],
        },
        beforeEnter: multiguard([requiredAnySitePermissions([Permission.Device_AssignDevice])]),
      },
      {
        path: "/sites",
        name: "all-sites",
        component: () => import("@/views/Sites/Sites.vue"),
        meta: { breadcrumbs: [{ titleKey: "breadcrumbs.sites" }] },
      },
      {
        path: "/sites/:siteId",
        name: "edit-site",
        component: () => import("@/views/Sites/EditSite.vue"),
        meta: {
          breadcrumbs: [
            { titleKey: "breadcrumbs.sites", href: "/sites" },
            { titleKey: "breadcrumbs.editSite" },
          ],
        },
        beforeEnter: multiguard([requiredSitePermissions([Permission.Site_UpdateSite])]),
      },
      {
        path: "/sites/:siteId/internal",
        name: "site-internal-settings",
        component: () => import("@/views/Sites/InternalSettings.vue"),
        meta: {
          breadcrumbs: [
            { titleKey: "breadcrumbs.sites", href: "/sites" },
            { titleKey: "breadcrumbs.internalSettings" },
          ],
        },
        beforeEnter: multiguard([userMustBeStaff]),
      },
      {
        path: "/new-site",
        name: "new-site",
        component: () => import("@/views/Sites/NewSite.vue"),
        meta: {
          breadcrumbs: [
            { titleKey: "breadcrumbs.sites", href: "/sites" },
            { titleKey: "breadcrumbs.createNewSite" },
          ],
        },
        beforeEnter: multiguard([requiredCompanyPermissions([Permission.Site_CreateSite])]),
      },
      {
        path: "/transactions",
        name: "transactions",
        component: () => import("@/views/Transactions.vue"),
        meta: {
          breadcrumbs: [
            { titleKey: "breadcrumbs.reporting" },
            { titleKey: "breadcrumbs.transactions" },
          ],
        },
      },
      {
        path: "/payouts",
        name: "payouts",
        component: () => import("@/views/Payouts/Payouts.vue"),
        meta: {
          breadcrumbs: [{ titleKey: "breadcrumbs.reporting" }, { titleKey: "breadcrumbs.payouts" }],
        },
      },
      {
        path: "/payouts/:payoutId",
        name: "payout-details",
        component: () => import("@/views/Payouts/PayoutLedger.vue"),
        meta: {
          breadcrumbs: [
            { titleKey: "breadcrumbs.reporting" },
            { titleKey: "breadcrumbs.payouts", href: "/payouts" },
            { titleKey: "breadcrumbs.ledger" },
          ],
        },
      },
      {
        path: "/reports",
        name: "reports",
        component: () => import("@/views/Reports.vue"),
        meta: {
          breadcrumbs: [{ titleKey: "breadcrumbs.reporting" }, { titleKey: "breadcrumbs.ledger" }],
        },
      },
      {
        path: "/eod-reports",
        name: "eod-reports",
        component: () => import("@/components/EODReports.vue"),
        meta: {
          breadcrumbs: [{ titleKey: "breadcrumbs.eod-reports" }],
        },
      },
      {
        path: "/reports/:reportId/download",
        name: "reports-download",
        component: () => import("@/views/Reports.vue"),
        meta: {
          breadcrumbs: [{ titleKey: "breadcrumbs.reporting" }, { titleKey: "breadcrumbs.ledger" }],
        },
      },
      {
        path: "/users",
        name: "users",
        component: () => import("@/views/Users/ManageUsers.vue"),
        meta: { breadcrumbs: [{ titleKey: "breadcrumbs.users" }] },
        beforeEnter: multiguard([requiredCompanyPermissions([Permission.User_ListUsers])]),
      },
      {
        path: "/users/:userId",
        name: "edit-user",
        component: () => import("@/views/Users/AddEditUser.vue"),
        meta: {
          breadcrumbs: [
            { titleKey: "breadcrumbs.users", href: "/users" },
            { titleKey: "breadcrumbs.editUser" },
          ],
        },
        beforeEnter: multiguard([requiredCompanyPermissions([Permission.User_UpdateUser])]),
      },
      {
        path: "/users/add",
        name: "add-user",
        component: () => import("@/views/Users/AddEditUser.vue"),
        meta: {
          breadcrumbs: [
            { titleKey: "breadcrumbs.users", href: "/users" },
            { titleKey: "breadcrumbs.addUser" },
          ],
        },
        beforeEnter: multiguard([requiredCompanyPermissions([Permission.User_InviteUser])]),
      },
      {
        path: "/company-settings",
        name: "company-settings",
        component: () => import("@/views/Companies/CompanySettings.vue"),
        meta: { breadcrumbs: [{ titleKey: "breadcrumbs.companySettings" }] },
        beforeEnter: multiguard([canManageCompanies]),
      },
      {
        path: "/company-settings/staff",
        name: "staff-company-settings",
        component: () => import("@/views/Companies/CompanySettingsStaff.vue"),
        meta: {
          breadcrumbs: [
            { titleKey: "breadcrumbs.companySettings", href: "/company-settings" },
            { titleKey: "breadcrumbs.staff" },
          ],
        },
        beforeEnter: multiguard([userMustBeStaff]),
      },
      {
        path: "/companies",
        name: "manage-companies",
        component: () => import("@/views/Companies/ListCompanies.vue"),
        meta: {
          breadcrumbs: [{ titleKey: "breadcrumbs.companies" }],
          hideBillingWarningBanner: true,
        },
        beforeEnter: multiguard([canManageCompanies]),
      },
    ],
  },
  {
    path: "/native-app",
    component: () => import("@/layouts/NativeApp.vue"),
    beforeEnter: multiguard([userMustBeNativeApp]),
    children: [
      {
        path: "/native-app/capital",
        name: "native-app-capital",
        beforeEnter: multiguard([userMustBeAuthenticatedNativeAppUser]),
        component: () => import("@/views/NativeApp/Capital.vue"),
      },
      {
        path: "/native-app/eod-reports",
        name: "native-app-eod-reports",
        beforeEnter: multiguard([userMustBeAuthenticatedNativeAppUser]),
        component: () => import("@/views/NativeApp/EODReportsNativeApp.vue"),
      },
    ],
  },
  {
    path: "/onboarding",
    component: () => import("@/layouts/Full.vue"),
    beforeEnter: multiguard([
      userMustBeAuthenticated,
      initialiseApplication,
      merchantMustNotBeVerified,
    ]),
    children: [
      {
        path: "/onboarding/pending",
        name: "invited-pending",
        component: () => import("@/views/Onboarding/InvitedPending.vue"),
      },
      {
        path: "/onboarding",
        name: "onboarding-landing",
        component: () => import("@/views/Onboarding/Landing.vue"),
        multiguard: [userMustBeConfigured],
      },
      {
        path: "/onboarding/wizard",
        name: "onboarding-wizard",
        component: () => import("@/views/Onboarding/Wizard.vue"),
        multiguard: [userMustBeConfigured],
      },
    ],
  },
  {
    path: "/assign-device",
    component: () => import("@/layouts/Full.vue"),
    beforeEnter: multiguard([
      userMustBeAuthenticated,
      initialiseApplication,
      requiredAnySitePermissions([Permission.Device_AssignDevice]),
    ]),
    children: [
      {
        path: "/assign-device",
        name: "assign-device",
        component: () => import("@/views/Devices/AssignDevice.vue"),
      },
    ],
  },
  {
    path: "/:path(.*)*",
    component: () => import("@/layouts/Full.vue"),
    children: [
      {
        path: "",
        name: "not-found",
        component: () => import("@/views/NotRight.vue"),
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach(async () => {
  // @ts-expect-error: i18n.global.locale.value is not typed
  const currentLocale = i18n.global.locale.value;

  if (currentLocale !== "en" && !i18n.global.availableLocales.includes(currentLocale)) {
    await loadLocaleMessages(currentLocale);
  }
});

export default router;
